import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Copy } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import AppLayout from '@/components/AppLayout';
import BackButton from '@/components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Library() {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContents();
  }, []);

  const fetchContents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/content/list`, {
        withCredentials: true,
      });
      setContents(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const deleteContent = async (contentId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/content/${contentId}`, {
        withCredentials: true,
      });
      setContents(contents.filter((c) => c.content_id !== contentId));
      toast.success('Contenu supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-8" data-testid="library-page">
        {/* Back Button */}
        <BackButton to="/dashboard" label="Retour au tableau de bord" className="mb-4" />
        
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-2">Bibliothèque</h1>
          <p className="text-muted-foreground">Tous vos contenus sauvegardés</p>
        </motion.div>

        {contents.length === 0 ? (
          <div className="glass rounded-xl p-12 text-center">
            <p className="text-muted-foreground">Aucun contenu sauvegardé pour le moment</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contents.map((content, index) => (
              <motion.div
                key={content.content_id}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                className="glass rounded-xl p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-xs font-medium text-primary uppercase">{content.platform}</span>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(content.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(content.content)}
                      data-testid={`copy-content-${content.content_id}`}
                    >
                      <Copy size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteContent(content.content_id)}
                      data-testid={`delete-content-${content.content_id}`}
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-4">{content.content}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
